import {
  Avatar,
  Badge,
  Button,
  ChannelIcon,
  channelMeta,
  EmptyState,
  formatDuration,
  formatPrice,
  shortName,
  type CommunicationChannel,
} from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { PhotoUpload } from "../components/photo-upload";
import { SalonContactPanel } from "../components/salon-contact";
import { DocumentsPanel } from "../components/documents";
import { ExportPlanning } from "../components/planning-export";
import { CreateStaffDialog, StaffEditor, type TeamMember } from "../components/team-editor";
import { SALON_ID, api } from "../lib/trpc";

type Tab =
  | "team"
  | "hours"
  | "timeOff"
  | "documents"
  | "reminders"
  | "catalog"
  | "portfolio"
  | "clients"
  | "site"
  | "audit";

const TABS: { key: Tab; label: string }[] = [
  { key: "team", label: "Équipe" },
  { key: "hours", label: "Horaires" },
  { key: "timeOff", label: "Congés" },
  // Range avec l'equipe et non avec le catalogue : contrats, paies et
  // reglements relevent des personnes, pas des prestations.
  { key: "documents", label: "Documents" },
  { key: "reminders", label: "Réservation" },
  { key: "catalog", label: "Prestations" },
  { key: "portfolio", label: "Réalisations" },
  { key: "clients", label: "Clientes" },
  { key: "site", label: "Site" },
  { key: "audit", label: "Journal" },
];

const WEEKDAYS = [
  { value: 1, label: "Lundi" },
  { value: 2, label: "Mardi" },
  { value: 3, label: "Mercredi" },
  { value: 4, label: "Jeudi" },
  { value: 5, label: "Vendredi" },
  { value: 6, label: "Samedi" },
  { value: 0, label: "Dimanche" },
];

/**
 * Réglages du salon.
 *
 * Volontairement rangé DERRIÈRE le quotidien : une gérante ouvre son planning
 * cinquante fois par jour et ses réglages une fois par mois. Les mettre au
 * même niveau dans la navigation aurait encombré les trois écrans qui comptent.
 */
export function Admin() {
  const [tab, setTab] = useState<Tab>("team");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 px-6 pb-2 pt-4">
        <h1 className="mb-3 text-2xl">Réglages</h1>
        <div className="flex flex-wrap gap-1">
          {TABS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              aria-current={tab === entry.key ? "page" : undefined}
              className={[
                "min-h-9 rounded-full px-4 text-sm transition-colors",
                tab === entry.key
                  ? "bg-blush-500 font-medium text-white"
                  : "text-encre-600 hover:bg-creme-200",
              ].join(" ")}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-8 pt-4">
        {tab === "team" && <TeamPanel />}
        {tab === "hours" && <HoursPanel />}
        {tab === "timeOff" && <TimeOffPanel />}
        {tab === "documents" && <DocumentsPanel />}
        {tab === "reminders" && <RemindersPanel />}
        {tab === "catalog" && <CatalogPanel />}
        {tab === "portfolio" && <PortfolioPanel />}
        {tab === "clients" && <ClientsPanel />}
        {tab === "site" && (
          <>
            <SalonContactPanel />
            <SitePanel />
          </>
        )}
        {tab === "audit" && <AuditPanel />}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Équipe                                                                     */
/* -------------------------------------------------------------------------- */

function TeamPanel() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);

  const team = useQuery({
    queryKey: ["admin", "team", showInactive],
    queryFn: () =>
      api.admin.team.list.query({ salonId: SALON_ID, includeInactive: showInactive }),
  });

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.admin.team.update.mutate>[0]) =>
      api.admin.team.update.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "team"] }),
  });

  const deactivate = useMutation({
    mutationFn: (staffId: string) => api.admin.team.deactivate.mutate({ staffId }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "team"] });
      if (result.upcomingAppointments > 0) {
        // On ne bloque pas, on avertit : ces RDV doivent être réattribués.
        window.alert(
          `${result.upcomingAppointments} rendez-vous à venir sont encore attribués à cette personne. Pensez à les déplacer.`,
        );
      }
    },
  });

  const reactivate = useMutation({
    mutationFn: (staffId: string) => api.admin.team.reactivate.mutate({ staffId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "team"] }),
  });

  return (
    <Panel
      title="Équipe"
      description="Désactiver une employée la retire du planning et du site, sans toucher à son historique."
      action={
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-encre-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            Afficher les inactives
          </label>
          <Button size="sm" onClick={() => setCreating(true)}>
            + Employée
          </Button>
        </div>
      }
    >
      {creating && <CreateStaffDialog onClose={() => setCreating(false)} />}
      {editing && <StaffEditor member={editing} onClose={() => setEditing(null)} />}

      {team.isPending && <p className="text-encre-400">Chargement…</p>}

      <ul className="space-y-2">
        {team.data?.map((member) => (
          <li
            key={member.id}
            className={[
              "flex flex-wrap items-center gap-4 rounded-xl bg-white p-4 shadow-soft",
              member.deletedAt ? "opacity-55" : "",
            ].join(" ")}
          >
            <Avatar src={member.avatarUrl} firstName={member.firstName} lastName={member.lastName} />

            <div className="min-w-0 flex-1">
              <p className="font-display text-lg leading-tight">
                {member.displayName}
                {member.deletedAt && (
                  <Badge tone="muted" className="ml-2">
                    Inactive
                  </Badge>
                )}
              </p>
              <p className="truncate text-sm text-encre-500">
                {member.role === "manager" ? "Gérante" : "Employée"} ·{" "}
                {contractLabel(member.contractType)}
                {member.weeklyContractMinutes
                  ? ` ${Math.round(member.weeklyContractMinutes / 60)} h/sem`
                  : ""}
                {member.specialties?.length ? ` · ${member.specialties.join(", ")}` : ""}
              </p>
            </div>

            <span
              aria-hidden
              className="size-4 shrink-0 rounded-full"
              style={{ backgroundColor: member.calendarColor }}
              title="Couleur dans le planning"
            />

            {member.deletedAt ? (
              <Button
                variant="secondary"
                size="sm"
                loading={reactivate.isPending}
                onClick={() => reactivate.mutate(member.id)}
              >
                Réactiver
              </Button>
            ) : (
              <>
                <label className="flex items-center gap-1.5 text-sm text-encre-600">
                  <input
                    type="checkbox"
                    checked={member.isBookableOnline}
                    onChange={(event) =>
                      update.mutate({
                        staffId: member.id,
                        isBookableOnline: event.target.checked,
                      })
                    }
                  />
                  Réservable en ligne
                </label>

                <Button variant="secondary" size="sm" onClick={() => setEditing(member)}>
                  Modifier
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deactivate.isPending}
                  onClick={() => {
                    if (window.confirm(`Désactiver ${member.displayName} ?`)) {
                      deactivate.mutate(member.id);
                    }
                  }}
                >
                  Désactiver
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/*  Horaires                                                                   */
/* -------------------------------------------------------------------------- */

/** « 2026-09-07 » → « lundi 7 septembre ». */
function libelleSemaine(lundi: string): string {
  const date = new Date(`${lundi}T12:00:00Z`);
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

/**
 * Horaires : deux semaines a planifier, plus la trame permanente.
 *
 * Le salon ne raisonne pas en « trame valable jusqu'a nouvel ordre » mais en
 * semaines concretes : qui vient quand, cette semaine et la suivante. L'ecran
 * propose donc ces deux semaines-la — et saute la semaine en cours des le
 * samedi, ou il ne reste plus rien a organiser.
 *
 * La trame permanente reste accessible, en troisieme onglet : c'est elle qui
 * s'applique a toutes les semaines qu'on n'a pas planifiees, et elle evite de
 * ressaisir les memes horaires chaque quinzaine.
 */
function HoursPanel() {
  const queryClient = useQueryClient();
  const [staffId, setStaffId] = useState<string | null>(null);
  /** `0` et `1` = les deux semaines proposees ; `"permanente"` = la trame. */
  const [onglet, setOnglet] = useState<0 | 1 | "permanente">(0);
  const [draft, setDraft] = useState<Record<number, { start: string; end: string }[]>>({});

  const team = useQuery({
    queryKey: ["admin", "team", false],
    queryFn: () => api.admin.team.list.query({ salonId: SALON_ID }),
  });

  const planning = useQuery({
    queryKey: ["admin", "planning-weeks"],
    queryFn: () => api.admin.team.planningWeeks.query({ salonId: SALON_ID }),
  });

  const selected = staffId ?? team.data?.[0]?.id ?? null;

  const permanente = useQuery({
    queryKey: ["admin", "hours", selected],
    queryFn: () => api.admin.team.hours.query({ salonId: SALON_ID, staffId: selected! }),
    enabled: Boolean(selected) && onglet === "permanente",
  });

  const membre = planning.data?.equipe.find((entry) => entry.id === selected);
  const semaine = typeof onglet === "number" ? membre?.semaines[onglet] : undefined;

  /*
   * Les semaines deja planifiees, qui PRIMENT sur la trame permanente.
   *
   * L'ecran doit le dire : sans cet avertissement, corriger la trame et ne rien
   * voir changer avant huit ou quinze jours passe pour un enregistrement rate.
   * On reessaie, on rappelle le salon — alors que la regle a parfaitement
   * fonctionne.
   */
  const semainesPlanifiees = (membre?.semaines ?? []).filter((entry) => entry.planifiee);

  /** Les plages en vigueur pour l'onglet courant, avant toute modification. */
  const plagesEnBase = (weekday: number): { start: string; end: string }[] => {
    if (onglet === "permanente") {
      return (permanente.data ?? [])
        .filter((row) => row.weekday === weekday)
        .map((row) => ({ start: row.start, end: row.end }))
        .sort((a, b) => a.start.localeCompare(b.start));
    }
    const jour = semaine?.jours.find((entry) => entry.weekday === weekday);
    /*
     * La plage nulle qui marque une semaine « sans travail » ne doit pas
     * remonter dans l'ecran : elle s'afficherait comme un 00:00 → 00:00 que
     * personne n'a saisi.
     */
    return (jour?.plages ?? []).filter((plage) => plage.start !== plage.end);
  };

  const slotsFor = (weekday: number) =>
    weekday in draft ? draft[weekday]! : plagesEnBase(weekday);

  const setSlots = (weekday: number, slots: { start: string; end: string }[]) =>
    setDraft((current) => ({ ...current, [weekday]: slots }));

  /**
   * Coupe une journée continue en deux, autour d'une pause déjeuner.
   *
   * Une heure, de 12 h à 13 h, par défaut : c'est la coupure la plus courante
   * au salon. Les deux bornes restent modifiables juste après.
   */
  const splitForLunch = (weekday: number) => {
    const [first] = slotsFor(weekday);
    if (!first) return;
    setSlots(weekday, [
      { start: first.start, end: "12:00" },
      { start: "13:00", end: first.end },
    ]);
  };

  /**
   * « 26/10 » à côté du jour, pour les semaines planifiées : on cale une
   * semaine précise, et l'écran doit dire laquelle sans faire le compte. La
   * trame permanente, elle, n'a pas de date.
   */
  const lundiAffiche = typeof onglet === "number" ? planning.data?.semaines[onglet]?.lundi : undefined;
  const dateDuJour = (weekday: number): string | null => {
    if (!lundiAffiche) return null;
    const jour = new Date(`${lundiAffiche}T12:00:00Z`);
    // `weekday` suit `getDay()` : 0 = dimanche, dernier jour de la semaine.
    jour.setUTCDate(jour.getUTCDate() + ((weekday + 6) % 7));
    return `${String(jour.getUTCDate()).padStart(2, "0")}/${String(jour.getUTCMonth() + 1).padStart(2, "0")}`;
  };

  const changerOnglet = (valeur: 0 | 1 | "permanente") => {
    setOnglet(valeur);
    // Le brouillon appartient a l'onglet qu'on quitte : le garder ecraserait
    // la semaine suivante avec des horaires saisis pour la precedente.
    setDraft({});
  };

  const save = useMutation({
    mutationFn: () => {
      const slots = WEEKDAYS.flatMap((day) =>
        slotsFor(day.value).map((slot) => ({
          weekday: day.value,
          start: slot.start,
          end: slot.end,
        })),
      );

      if (onglet === "permanente") {
        return api.admin.team.replaceHours.mutate({
          salonId: SALON_ID,
          staffId: selected!,
          effectiveFrom: new Date().toISOString().slice(0, 10),
          slots,
        });
      }

      return api.admin.team.planWeek.mutate({
        salonId: SALON_ID,
        staffId: selected!,
        monday: planning.data!.semaines[onglet].lundi,
        slots,
      });
    },
    onSuccess: () => {
      setDraft({});
      queryClient.invalidateQueries({ queryKey: ["admin", "hours"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "planning-weeks"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  /** Reprend la trame permanente comme point de depart de la semaine. */
  const partirDeLaPermanente = async () => {
    const trame = await api.admin.team.hours.query({ salonId: SALON_ID, staffId: selected! });
    const parJour: Record<number, { start: string; end: string }[]> = {};
    for (const day of WEEKDAYS) {
      parJour[day.value] = trame
        .filter((row) => row.weekday === day.value)
        .map((row) => ({ start: row.start, end: row.end }))
        .sort((a, b) => a.start.localeCompare(b.start));
    }
    setDraft(parJour);
  };

  const onglets: { valeur: 0 | 1 | "permanente"; label: string; note?: string }[] = [
    ...(planning.data?.semaines ?? []).map((entry, index) => ({
      valeur: index as 0 | 1,
      label: `Semaine du ${libelleSemaine(entry.lundi)}`,
      note: membre?.semaines[index]?.planifiee ? "planifiée" : "trame permanente",
    })),
    { valeur: "permanente" as const, label: "Trame permanente" },
  ];

  return (
    <Panel
      title="Horaires"
      description="Planifiez les deux prochaines semaines. Ce qui n'est pas planifié suit la trame permanente."
      action={
        <div className="flex items-center gap-2">
          {planning.data && (
            <ExportPlanning
              dateKey={planning.data.semaines[0].lundi}
              libellePremiere="Première semaine"
            />
          )}
          <select
            value={selected ?? ""}
            onChange={(event) => {
              setStaffId(event.target.value);
              setDraft({});
            }}
            className="h-10 rounded-lg border border-creme-300 bg-white px-3 text-sm"
          >
            {team.data?.map((member) => (
              <option key={member.id} value={member.id}>
                {member.displayName}
              </option>
            ))}
          </select>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap gap-1">
        {onglets.map((entry) => (
          <button
            key={String(entry.valeur)}
            type="button"
            onClick={() => changerOnglet(entry.valeur)}
            aria-current={onglet === entry.valeur ? "page" : undefined}
            className={[
              "rounded-full px-4 py-2 text-left text-sm transition-colors",
              onglet === entry.valeur
                ? "bg-blush-500 text-white"
                : "bg-creme-100 text-encre-700 hover:bg-creme-200",
            ].join(" ")}
          >
            <span className="block first-letter:uppercase">{entry.label}</span>
            {entry.note && (
              <span
                className={[
                  "block text-[11px]",
                  onglet === entry.valeur ? "text-white/75" : "text-encre-400",
                ].join(" ")}
              >
                {entry.note}
              </span>
            )}
          </button>
        ))}
      </div>

      {onglet === "permanente" && semainesPlanifiees.length > 0 && (
        <p className="mb-3 rounded-lg bg-ambre-100 px-4 py-2.5 text-sm text-ambre-700">
          {semainesPlanifiees.length > 1 ? "Les semaines du " : "La semaine du "}
          {semainesPlanifiees.map((entry) => libelleSemaine(entry.lundi)).join(" et du ")}
          {semainesPlanifiees.length > 1 ? " sont déjà planifiées" : " est déjà planifiée"} : cette
          trame ne s'y appliquera pas. Ces horaires-là se modifient depuis leur propre onglet.
        </p>
      )}

      {typeof onglet === "number" && membre && !membre.semaines[onglet]?.planifiee && (
        <p className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-ambre-100 px-4 py-2.5 text-sm text-ambre-700">
          Cette semaine suit la trame permanente. Enregistrez pour la fixer.
          <Button size="sm" variant="ghost" onClick={partirDeLaPermanente}>
            Repartir de la trame
          </Button>
        </p>
      )}

      <div className="max-w-lg space-y-2">
        {WEEKDAYS.map((day) => {
          const slots = slotsFor(day.value);
          const worked = slots.length > 0;

          return (
            <div key={day.value} className="rounded-lg bg-white p-3 shadow-soft">
              <div className="flex items-center gap-3">
                <label className="flex w-40 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={worked}
                    onChange={(event) =>
                      setSlots(day.value, event.target.checked ? [{ start: "09:00", end: "19:00" }] : [])
                    }
                  />
                  <span className="text-encre-800">
                    {day.label}
                    {dateDuJour(day.value) && (
                      <span className="ml-1.5 tabular-nums text-encre-400">
                        {dateDuJour(day.value)}
                      </span>
                    )}
                  </span>
                </label>

                {!worked && <span className="text-sm text-encre-400">Repos</span>}

                {slots.length === 1 && (
                  <Button variant="ghost" size="sm" onClick={() => splitForLunch(day.value)}>
                    Couper pour le midi
                  </Button>
                )}
              </div>

              {worked && (
                <div className="mt-2 space-y-2 pl-8">
                  {slots.map((slot, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={slot.start}
                        onChange={(event) =>
                          setSlots(
                            day.value,
                            slots.map((entry, position) =>
                              position === index ? { ...entry, start: event.target.value } : entry,
                            ),
                          )
                        }
                        className="h-9 rounded-lg border border-creme-300 px-2 tabular-nums"
                      />
                      <span className="text-encre-400">→</span>
                      <input
                        type="time"
                        value={slot.end}
                        onChange={(event) =>
                          setSlots(
                            day.value,
                            slots.map((entry, position) =>
                              position === index ? { ...entry, end: event.target.value } : entry,
                            ),
                          )
                        }
                        className="h-9 rounded-lg border border-creme-300 px-2 tabular-nums"
                      />

                      {index > 0 && (
                        <span className="text-xs text-encre-400">
                          pause de {slots[index - 1]!.end} à {slot.start}
                        </span>
                      )}

                      {slots.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSlots(
                              day.value,
                              slots.filter((_, position) => position !== index),
                            )
                          }
                        >
                          Retirer
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {save.isError && (
          <p role="alert" className="text-sm font-medium text-grenat-700">
            {save.error instanceof Error ? save.error.message : "Enregistrement impossible."}
          </p>
        )}

        <Button loading={save.isPending} disabled={!selected} onClick={() => save.mutate()}>
          {onglet === "permanente" ? "Enregistrer la trame" : "Enregistrer la semaine"}
        </Button>

        {save.isSuccess && Object.keys(draft).length === 0 && (
          <p className="text-sm text-sauge-700">Horaires enregistrés.</p>
        )}
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/*  Congés                                                                     */
/* -------------------------------------------------------------------------- */

const ABSENCE_TYPES = [
  { value: "vacation", label: "Congé payé", deducts: true },
  { value: "sick_leave", label: "Arrêt maladie", deducts: false },
  { value: "work_accident", label: "Accident du travail", deducts: false },
  { value: "family_event", label: "Événement familial", deducts: false },
  { value: "parental", label: "Congé parental", deducts: false },
  { value: "training", label: "Formation", deducts: false },
  { value: "personal", label: "Congé sans solde", deducts: false },
  { value: "unjustified", label: "Absence injustifiée", deducts: false },
  { value: "salon_closed", label: "Fermeture du salon", deducts: false },
] as const;

function TimeOffPanel() {
  const queryClient = useQueryClient();
  const from = new Date();
  const to = new Date(from.getTime() + 180 * 86_400_000);
  const [showForm, setShowForm] = useState(false);

  const team = useQuery({
    queryKey: ["admin", "team", false],
    queryFn: () => api.admin.team.list.query({ salonId: SALON_ID }),
  });

  const balances = useQuery({
    queryKey: ["admin", "balances"],
    queryFn: () => api.admin.team.leaveBalances.query({ salonId: SALON_ID }),
  });

  const absences = useQuery({
    queryKey: ["admin", "timeOff"],
    queryFn: () => api.admin.team.timeOff.query({ salonId: SALON_ID, from, to }),
  });

  const review = useMutation({
    mutationFn: (input: { timeOffId: string; approve: boolean }) =>
      api.admin.team.reviewTimeOff.mutate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "timeOff"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const cancel = useMutation({
    mutationFn: (timeOffId: string) => api.admin.team.cancelTimeOff.mutate({ timeOffId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "timeOff"] }),
  });

  const pending = absences.data?.filter((row) => row.status === "pending") ?? [];
  const approved = absences.data?.filter((row) => row.status === "approved") ?? [];

  return (
    <>
      {/*
        Les soldes d'abord : c'est la question qu'on se pose AVANT de valider
        une demande, pas apres.
      */}
      <Panel
        title="Soldes de congés"
        description="Année de référence du 1er juin au 31 mai. Seuls les congés payés amputent le solde — ni la maladie, ni une absence injustifiée."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {balances.data?.map((row) => (
            <div key={row.staffId} className="rounded-xl bg-white p-4 shadow-soft">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-display text-lg">{row.displayName}</p>
                <Badge tone="neutral">{contractLabel(row.contractType)}</Badge>
              </div>

              <p className="mt-2 font-display text-3xl text-encre-900" data-numeric>
                {row.balance.daysRemaining} j
                <span className="ml-1 text-base font-normal text-encre-400">
                  / {row.balance.daysGranted + row.balance.daysCarriedOver}
                </span>
              </p>

              <p className="text-sm text-encre-500" data-numeric>
                {row.balance.daysTaken} pris
                {row.balance.daysPending > 0 && (
                  <span className="text-ambre-700"> · {row.balance.daysPending} en attente</span>
                )}
              </p>

              {Object.entries(row.balance.byType).filter(([type]) => type !== "vacation").length >
                0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.entries(row.balance.byType)
                    .filter(([type]) => type !== "vacation")
                    .map(([type, days]) => (
                      <Badge key={type} tone={type === "unjustified" ? "danger" : "muted"}>
                        {absenceLabel(type)} : {days} j
                      </Badge>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>

    <Panel
      title="Congés et absences"
      description="Un congé validé libère immédiatement les créneaux correspondants."
      action={
        <Button size="sm" onClick={() => setShowForm((value) => !value)}>
          {showForm ? "Fermer" : "+ Saisir une absence"}
        </Button>
      }
    >
      {showForm && (
        <AbsenceForm
          team={team.data ?? []}
          onSaved={() => {
            setShowForm(false);
            queryClient.invalidateQueries({ queryKey: ["admin", "timeOff"] });
            queryClient.invalidateQueries({ queryKey: ["admin", "balances"] });
            queryClient.invalidateQueries({ queryKey: ["planning"] });
          }}
        />
      )}

      {pending.length > 0 && (
        <>
          <h3 className="mb-2 text-lg">À valider</h3>
          <ul className="mb-6 space-y-2">
            {pending.map((row) => (
              <li key={row.id} className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-soft">
                <div className="min-w-0 flex-1">
                  <p className="text-encre-900">
                    {row.staffName ?? "Salon entier"} · {absenceLabel(row.type)}
                  </p>
                  <p className="text-sm text-encre-500" data-numeric>
                    {formatRange(row.startsAt, row.endsAt)}
                    {row.reason ? ` — ${row.reason}` : ""}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => review.mutate({ timeOffId: row.id, approve: false })}
                >
                  Refuser
                </Button>
                <Button size="sm" onClick={() => review.mutate({ timeOffId: row.id, approve: true })}>
                  Valider
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 className="mb-2 text-lg">À venir</h3>
      {approved.length === 0 ? (
        <EmptyState
          variant="card"
          glyph="🌴"
          title="Aucune absence prévue"
          description="Les congés validés apparaîtront ici et dans le planning."
        />
      ) : (
        <ul className="space-y-2">
          {approved.map((row) => (
            <li key={row.id} className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-soft">
              <div className="min-w-0 flex-1">
                <p className="text-encre-900">
                  {row.staffName ?? "Salon entier"} · {absenceLabel(row.type)}
                </p>
                <p className="text-sm text-encre-500" data-numeric>
                  {formatRange(row.startsAt, row.endsAt)}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => cancel.mutate(row.id)}>
                Annuler
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
    </>
  );
}

/**
 * Saisie d'une absence.
 *
 * Le nombre de jours decomptes n'est PAS saisi : il est calcule cote serveur
 * sur la trame horaire reelle de l'employee. Poser du vendredi au lundi ne
 * consomme pas quatre jours si elle est en repos le week-end.
 */
function AbsenceForm({
  team,
  onSaved,
}: {
  team: { id: string; displayName: string }[];
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [draft, setDraft] = useState({
    staffId: team[0]?.id ?? "",
    type: "vacation" as (typeof ABSENCE_TYPES)[number]["value"],
    startDate: today,
    endDate: today,
    isAllDay: true,
    reason: "",
    justificationUrl: "",
  });

  const rule = ABSENCE_TYPES.find((entry) => entry.value === draft.type);

  const save = useMutation({
    mutationFn: () =>
      api.admin.team.requestTimeOff.mutate({
        salonId: SALON_ID,
        // « Fermeture du salon » concerne tout le monde : pas d'employee.
        staffId: draft.type === "salon_closed" ? null : draft.staffId,
        type: draft.type,
        startsAt: new Date(`${draft.startDate}T00:00:00`),
        endsAt: new Date(`${draft.endDate}T23:59:00`),
        isAllDay: draft.isAllDay,
        reason: draft.reason || undefined,
        justificationUrl: draft.justificationUrl || undefined,
      }),
    onSuccess: onSaved,
  });

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form
      className="mb-6 grid gap-3 rounded-xl bg-white p-4 shadow-soft sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Type d'absence">
        <select
          value={draft.type}
          onChange={(event) => set("type", event.target.value as typeof draft.type)}
          className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
        >
          {ABSENCE_TYPES.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
      </Field>

      {draft.type !== "salon_closed" && (
        <Field label="Employée">
          <select
            value={draft.staffId}
            onChange={(event) => set("staffId", event.target.value)}
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
          >
            {team.map((member) => (
              <option key={member.id} value={member.id}>
                {member.displayName}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Du">
        <input
          type="date"
          value={draft.startDate}
          onChange={(event) => set("startDate", event.target.value)}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <Field label="Au">
        <input
          type="date"
          value={draft.endDate}
          min={draft.startDate}
          onChange={(event) => set("endDate", event.target.value)}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm text-encre-700">
        <input
          type="checkbox"
          checked={draft.isAllDay}
          onChange={(event) => set("isAllDay", event.target.checked)}
        />
        Journée entière (sinon comptée pour une demi-journée)
      </label>

      <Field label="Motif (facultatif)">
        <input
          value={draft.reason}
          onChange={(event) => set("reason", event.target.value)}
          className="h-10 w-full rounded-lg border border-creme-300 px-3"
        />
      </Field>

      {(draft.type === "sick_leave" || draft.type === "work_accident") && (
        <Field label="Lien du justificatif" className="sm:col-span-2">
          <input
            value={draft.justificationUrl}
            onChange={(event) => set("justificationUrl", event.target.value)}
            placeholder="https://… (sans lui, l'absence reste à relancer)"
            className="h-10 w-full rounded-lg border border-creme-300 px-3"
          />
        </Field>
      )}

      <p className="text-sm text-encre-500 sm:col-span-2">
        {rule?.deducts
          ? "Cette absence sera décomptée du solde de congés payés."
          : "Cette absence ne touche pas au solde de congés payés."}
      </p>

      {save.isError && (
        <p role="alert" className="text-sm font-medium text-grenat-700 sm:col-span-2">
          {save.error instanceof Error ? save.error.message : "Saisie impossible."}
        </p>
      )}

      <div className="sm:col-span-2">
        <Button type="submit" loading={save.isPending}>
          Enregistrer l'absence
        </Button>
      </div>
    </form>
  );
}

const CONTRACT_LABELS: Record<string, string> = {
  cdi: "CDI",
  cdd: "CDD",
  apprentissage: "Apprentissage",
  stage: "Stage",
  freelance: "Indépendante",
};
/*
 * « Sans contrat » et non « Contrat non renseigne ».
 *
 * L'absence de contrat est desormais un etat choisi — une gerante mandataire,
 * une independante — et non un oubli de saisie. Le libelle precedent affichait
 * un reproche a chaque ligne.
 */
const contractLabel = (type: string | null) =>
  type ? (CONTRACT_LABELS[type] ?? type) : "Sans contrat";

/* -------------------------------------------------------------------------- */
/*  Prestations                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Prestation vierge, pour la creation.
 *
 * Les tampons a 10 minutes ne sont pas anodins : ils empechent deux rendez-vous
 * de se toucher au point de deborder l'un sur l'autre. Une prestation creee
 * avec des tampons a zero remplit le planning de creneaux impossibles a tenir.
 */
const NOUVELLE_PRESTATION = {
  name: "",
  tagline: null,
  durationMinutes: 60,
  priceCents: 0,
  bufferBeforeMinutes: 10,
  bufferAfterMinutes: 10,
  isPriceFrom: false,
  isActive: true,
};

function CatalogPanel() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const services = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api.admin.catalog.services.query({ salonId: SALON_ID }),
  });

  const categories = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => api.admin.catalog.categories.query({ salonId: SALON_ID }),
  });

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.admin.catalog.saveService.mutate>[0]) =>
      api.admin.catalog.saveService.mutate(input),
    onSuccess: () => {
      setEditing(null);
      setCreating(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
    },
  });

  const archive = useMutation({
    mutationFn: (input: { serviceId: string; force?: boolean }) =>
      api.admin.catalog.archiveService.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "services"] }),
    onError: (error) => {
      // Le serveur refuse tant que des RDV futurs l'utilisent : on relaie la
      // question plutôt que d'imposer.
      if (error instanceof Error && window.confirm(`${error.message}\n\nRetirer quand même ?`)) {
        const id = archive.variables?.serviceId;
        if (id) archive.mutate({ serviceId: id, force: true });
      }
    },
  });

  return (
    <Panel
      title="Prestations"
      description="Les tarifs affichés sur le site viennent d'ici. Une modification est visible immédiatement."
      action={
        <Button size="sm" onClick={() => setCreating(true)} disabled={creating}>
          + Prestation
        </Button>
      }
    >
      <CategoriesEditor categories={categories.data ?? []} />

      {services.isPending && <p className="text-encre-400">Chargement…</p>}

      {/* Sans ce formulaire, un salon neuf ne pouvait creer AUCUNE prestation :
          l'ecran n'offrait qu'un bouton « Modifier » par ligne existante, et
          la liste etait vide. Impasse complete au premier demarrage. */}
      {creating && (
        <div className="mb-2 rounded-xl bg-white p-4 shadow-soft">
          <ServiceForm
            service={NOUVELLE_PRESTATION}
            categories={categories.data ?? []}
            pending={save.isPending}
            onCancel={() => setCreating(false)}
            onSave={(values) => save.mutate({ ...values, salonId: SALON_ID })}
          />
        </div>
      )}

      {services.data?.length === 0 && !creating && (
        <EmptyState
          glyph="💅"
          title="Aucune prestation"
          description="Le tunnel de réservation et les tarifs du site en dépendent : commencez par là."
        />
      )}

      <ul className="space-y-2">
        {services.data?.map((service) => (
          <li key={service.id} className="rounded-xl bg-white p-4 shadow-soft">
            {editing === service.id ? (
              <ServiceForm
                service={service}
                categories={categories.data ?? []}
                pending={save.isPending}
                onCancel={() => setEditing(null)}
                onSave={(values) => save.mutate({ ...values, id: service.id, salonId: SALON_ID })}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg leading-tight">
                    {service.name}
                    {service.isAddOn && (
                      <Badge tone="neutral" className="ml-2">
                        Option
                      </Badge>
                    )}
                    {!service.isActive && (
                      <Badge tone="muted" className="ml-2">
                        Masquée
                      </Badge>
                    )}
                  </p>
                  <p className="text-sm text-encre-500">
                    {service.category?.name ?? "Sans catégorie"} ·{" "}
                    {formatDuration(service.durationMinutes)} ·{" "}
                    {formatPrice(service.priceCents, { from: service.isPriceFrom })}
                    {service.bufferBeforeMinutes + service.bufferAfterMinutes > 0
                      ? ` · +${service.bufferBeforeMinutes}/${service.bufferAfterMinutes} min de battement`
                      : ""}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setEditing(service.id)}>
                  Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => archive.mutate({ serviceId: service.id })}
                >
                  Retirer
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * Categories de prestations — les regroupements de la grille tarifaire.
 *
 * L'API savait les creer depuis le debut, aucun ecran ne le permettait : un
 * salon neuf n'avait donc aucun moyen d'obtenir autre chose qu'une liste plate
 * sous « Sans categorie ».
 */
function CategoriesEditor({ categories }: { categories: { id: string; name: string }[] }) {
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState("");

  const enregistrer = useMutation({
    mutationFn: (input: { id?: string; name: string }) =>
      api.admin.catalog.saveCategory.mutate({ ...input, salonId: SALON_ID }),
    onSuccess: () => {
      setNom("");
      queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
      // Les prestations affichent le nom de leur categorie : il vient de
      // changer.
      queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
    },
  });

  return (
    <div className="mb-4 rounded-xl border border-creme-200 bg-creme-50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-encre-600">Catégories</span>

        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            title="Renommer"
            onClick={() => {
              const nouveau = window.prompt("Nom de la catégorie", category.name);
              if (nouveau && nouveau.trim() && nouveau !== category.name) {
                enregistrer.mutate({ id: category.id, name: nouveau.trim() });
              }
            }}
            className="rounded-full bg-creme-200 px-3 py-1 text-sm text-encre-800 hover:bg-creme-300"
          >
            {category.name}
          </button>
        ))}

        {categories.length === 0 && (
          <span className="text-sm text-encre-400">
            Aucune — les prestations s&apos;afficheront à plat sur le site.
          </span>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          onClick={() => setOuvert((etat) => !etat)}
        >
          {ouvert ? "Fermer" : "+ Catégorie"}
        </Button>
      </div>

      {ouvert && (
        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (nom.trim()) enregistrer.mutate({ name: nom.trim() });
          }}
        >
          <input
            value={nom}
            onChange={(event) => setNom(event.target.value)}
            placeholder="Mains, Pieds, Nail art…"
            className="h-9 flex-1 rounded-lg border border-creme-300 px-3 text-sm"
          />
          <Button size="sm" loading={enregistrer.isPending} type="submit">
            Créer
          </Button>
        </form>
      )}

      {enregistrer.isError && (
        <p role="alert" className="mt-2 text-sm text-grenat-700">
          {enregistrer.error instanceof Error ? enregistrer.error.message : "Échec."}
        </p>
      )}
    </div>
  );
}

type ServiceDraft = {
  /** `null` = « Sans catégorie » : la prestation reste vendable, simplement
   *  isolee en bas de la grille tarifaire du site. */
  categoryId: string | null;
  name: string;
  durationMinutes: number;
  priceCents: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  isPriceFrom: boolean;
  isActive: boolean;
  tagline?: string | null;
};

function ServiceForm({
  service,
  categories,
  pending,
  onSave,
  onCancel,
}: {
  service: {
    categoryId?: string | null;
    name: string;
    tagline: string | null;
    durationMinutes: number;
    priceCents: number;
    bufferBeforeMinutes: number;
    bufferAfterMinutes: number;
    isPriceFrom: boolean;
    isActive: boolean;
  };
  categories: { id: string; name: string }[];
  pending: boolean;
  onSave: (values: ServiceDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ServiceDraft>({
    categoryId: service.categoryId ?? null,
    name: service.name,
    tagline: service.tagline,
    durationMinutes: service.durationMinutes,
    priceCents: service.priceCents,
    bufferBeforeMinutes: service.bufferBeforeMinutes,
    bufferAfterMinutes: service.bufferAfterMinutes,
    isPriceFrom: service.isPriceFrom,
    isActive: service.isActive,
  });

  const set = <K extends keyof ServiceDraft>(key: K, value: ServiceDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      {/*
        La categorie EST le regroupement de la grille tarifaire du site :
        « Mains », « Pieds », « Suppléments ». Elle etait absente de ce
        formulaire, et l'API l'acceptait pourtant depuis toujours — une
        prestation creee ici tombait donc invariablement dans « Sans
        categorie », sans qu'aucun ecran ne permette de l'en sortir.
      */}
      <Field label="Catégorie">
        <select
          value={draft.categoryId ?? ""}
          onChange={(event) => set("categoryId", event.target.value || null)}
          className="h-10 w-full rounded-lg border border-creme-300 px-3"
        >
          <option value="">Sans catégorie</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Nom">
        <input
          value={draft.name}
          onChange={(event) => set("name", event.target.value)}
          required
          className="h-10 w-full rounded-lg border border-creme-300 px-3"
        />
      </Field>

      <Field label="Accroche (facultatif)" className="sm:col-span-2">
        <input
          value={draft.tagline ?? ""}
          onChange={(event) => set("tagline", event.target.value)}
          maxLength={90}
          className="h-10 w-full rounded-lg border border-creme-300 px-3"
        />
      </Field>

      <Field label="Durée (minutes)">
        <input
          type="number"
          min={5}
          max={600}
          step={5}
          value={draft.durationMinutes}
          onChange={(event) => set("durationMinutes", Number(event.target.value))}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <Field label="Prix (euros)">
        <input
          type="number"
          min={0}
          step={0.5}
          value={draft.priceCents / 100}
          onChange={(event) => set("priceCents", Math.round(Number(event.target.value) * 100))}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <Field label="Préparation avant (min)">
        <input
          type="number"
          min={0}
          max={120}
          step={5}
          value={draft.bufferBeforeMinutes}
          onChange={(event) => set("bufferBeforeMinutes", Number(event.target.value))}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <Field label="Séchage après (min)">
        <input
          type="number"
          min={0}
          max={120}
          step={5}
          value={draft.bufferAfterMinutes}
          onChange={(event) => set("bufferAfterMinutes", Number(event.target.value))}
          className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm text-encre-700">
        <input
          type="checkbox"
          checked={draft.isPriceFrom}
          onChange={(event) => set("isPriceFrom", event.target.checked)}
        />
        Prix « à partir de »
      </label>

      <label className="flex items-center gap-2 text-sm text-encre-700">
        <input
          type="checkbox"
          checked={draft.isActive}
          onChange={(event) => set("isActive", event.target.checked)}
        />
        Visible sur le site
      </label>

      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" loading={pending}>
          Enregistrer
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Clientes                                                                   */
/* -------------------------------------------------------------------------- */

export function ClientsPanel() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  /** `"new"` ouvre une fiche vierge, un identifiant ouvre la fiche existante. */
  const [editing, setEditing] = useState<string | null>(null);

  const clients = useQuery({
    queryKey: ["admin", "clients", search],
    queryFn: () =>
      api.admin.clients.list.query({ salonId: SALON_ID, search: search || undefined }),
  });

  const unlinked = useQuery({
    queryKey: ["admin", "unlinked"],
    queryFn: () => api.admin.clients.unlinkedIdentities.query({ salonId: SALON_ID }),
  });

  const link = useMutation({
    mutationFn: (input: { identityId: string; clientId: string }) =>
      api.admin.clients.linkIdentity.mutate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "unlinked"] });
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
  });

  return (
    <>
      {/*
        Le rapprochement d'abord : c'est la seule action ici qui a une valeur
        immédiate pour l'équipe. Une conversation anonyme est une cliente qu'on
        ne reconnaît pas au moment de lui répondre.
      */}
      {(unlinked.data?.length ?? 0) > 0 && (
        <Panel
          title="Contacts à rapprocher"
          description="Ces conversations ne sont rattachées à aucune fiche. Instagram ne fournit ni téléphone ni e-mail : le rapprochement reste un geste humain."
        >
          <ul className="space-y-2">
            {unlinked.data?.map((identity) => (
              <IdentityRow key={identity.id} identity={identity} onLink={link.mutate} />
            ))}
          </ul>
        </Panel>
      )}

      <Panel
        title="Clientes"
        description="Historique, préférences et canaux de contact, réunis sur une fiche."
        action={
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nom, téléphone, e-mail…"
              className="h-10 w-64 rounded-lg border border-creme-300 px-3 text-sm"
            />
            <Button size="sm" onClick={() => setEditing("new")}>
              + Cliente
            </Button>
          </div>
        }
      >
        {clients.data && clients.data.items.length === 0 && (
          <EmptyState
            variant="card"
            glyph={search ? "🔍" : "🌸"}
            title={search ? "Aucune cliente trouvée" : "Aucune fiche pour l'instant"}
            description={
              search
                ? "Essayez avec moins de caractères."
                : "Les fiches se créent seules à la première réservation. Vous pouvez aussi en ajouter une à la main."
            }
          />
        )}

        <ul className="space-y-2">
          {clients.data?.items.map((client) => (
            <li key={client.id}>
              {/*
                La ligne entière est cliquable : chercher une cliente puis
                devoir viser un petit crayon est un geste de trop au comptoir.
              */}
              <button
                type="button"
                onClick={() => setEditing(client.id)}
                className="flex w-full flex-wrap items-center gap-4 rounded-xl bg-white p-4 text-left shadow-soft transition-shadow hover:shadow-lift"
              >
                <Avatar src={client.avatarUrl} firstName={client.firstName} lastName={client.lastName} />
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg leading-tight">
                    {shortName(client.firstName, client.lastName)}
                    {client.isVip && (
                      <Badge tone="accent" className="ml-2">
                        VIP
                      </Badge>
                    )}
                    {(client.noShowCount ?? 0) >= 2 && (
                      <Badge tone="danger" className="ml-2">
                        {client.noShowCount} absences
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-sm text-encre-500">
                    {client.phone ?? client.email ?? "—"} · {client.visitCount ?? 0} visite(s) ·{" "}
                    {formatPrice(client.lifetimeValueCents ?? 0)}
                  </p>
                  {client.allergies && client.allergies.length > 0 && (
                    <p className="text-sm font-medium text-grenat-700">
                      ⚠️ {client.allergies.join(", ")}
                    </p>
                  )}
                </div>
                <span aria-hidden className="text-encre-300">
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>

        {clients.data && (
          <p className="mt-3 text-sm text-encre-400">
            {clients.data.items.length} affichée(s) sur {clients.data.total}
          </p>
        )}
      </Panel>

      {editing && (
        <ClientEditor
          clientId={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Fiche cliente en création ou en modification.
 *
 * La fiche vit sur deux tables : l'identité sur `users`, tout ce qui relève du
 * salon sur `client_profiles`. `admin.clients.save` écrit les deux d'une seule
 * transaction — d'où un formulaire unique plutôt que deux écrans.
 *
 * Les allergies se saisissent en texte libre séparé par des virgules : c'est
 * ce que l'équipe dicte au téléphone, et le seul format qu'elle retient.
 */
function ClientEditor({ clientId, onClose }: { clientId: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["admin", "client", clientId],
    queryFn: () => api.admin.clients.detail.query({ clientId: clientId! }),
    enabled: clientId !== null,
  });

  const [draft, setDraft] = useState<{
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    birthDate: string;
    allergies: string;
    privateNote: string;
    isVip: boolean;
    marketingOptIn: boolean;
  } | null>(clientId === null ? emptyClientDraft() : null);

  // La fiche arrive après coup : on n'initialise qu'une fois, sinon toute
  // frappe serait écrasée par la réponse du serveur.
  if (draft === null && detail.data) {
    const { client } = detail.data;
    setDraft({
      firstName: client.firstName,
      lastName: client.lastName ?? "",
      phone: client.phone ?? "",
      email: client.email ?? "",
      birthDate: client.birthDate ?? "",
      allergies: (client.clientProfile?.allergies ?? []).join(", "),
      privateNote: client.clientProfile?.privateNote ?? "",
      isVip: client.clientProfile?.isVip ?? false,
      marketingOptIn: client.marketingOptIn,
    });
  }

  const set = <K extends keyof NonNullable<typeof draft>>(
    key: K,
    value: NonNullable<typeof draft>[K],
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const save = useMutation({
    mutationFn: () =>
      api.admin.clients.save.mutate({
        clientId: clientId ?? undefined,
        salonId: SALON_ID,
        firstName: draft!.firstName.trim(),
        lastName: draft!.lastName.trim() || null,
        phone: draft!.phone.trim() || null,
        email: draft!.email.trim() || null,
        birthDate: draft!.birthDate || null,
        marketingOptIn: draft!.marketingOptIn,
        allergies: draft!.allergies
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean),
        privateNote: draft!.privateNote.trim() || null,
        isVip: draft!.isVip,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
      onClose();
    },
  });

  const title = clientId === null ? "Nouvelle cliente" : "Fiche cliente";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-2xl">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>

        {draft === null ? (
          <p className="text-encre-400">Chargement de la fiche…</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Prénom">
                <input
                  value={draft.firstName}
                  onChange={(event) => set("firstName", event.target.value)}
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
              <Field label="Nom">
                <input
                  value={draft.lastName}
                  onChange={(event) => set("lastName", event.target.value)}
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
              <Field label="Téléphone">
                <input
                  value={draft.phone}
                  onChange={(event) => set("phone", event.target.value)}
                  inputMode="tel"
                  placeholder="06 12 34 56 78"
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
              <Field label="E-mail">
                <input
                  value={draft.email}
                  onChange={(event) => set("email", event.target.value)}
                  inputMode="email"
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
              <Field label="Date de naissance">
                <input
                  type="date"
                  value={draft.birthDate}
                  onChange={(event) => set("birthDate", event.target.value)}
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
              <Field label="Allergies et contre-indications">
                <input
                  value={draft.allergies}
                  onChange={(event) => set("allergies", event.target.value)}
                  placeholder="Résine, latex…"
                  className="h-11 w-full rounded-lg border border-creme-300 px-3"
                />
              </Field>
            </div>

            <Field label="Note interne" className="mt-3 block">
              <textarea
                value={draft.privateNote}
                onChange={(event) => set("privateNote", event.target.value)}
                rows={3}
                placeholder="Jamais visible côté cliente."
                className="w-full rounded-lg border border-creme-300 p-3"
              />
            </Field>

            <div className="mt-3 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-encre-700">
                <input
                  type="checkbox"
                  checked={draft.isVip}
                  onChange={(event) => set("isVip", event.target.checked)}
                />
                Cliente VIP
              </label>
              <label className="flex items-center gap-2 text-sm text-encre-700">
                <input
                  type="checkbox"
                  checked={draft.marketingOptIn}
                  onChange={(event) => set("marketingOptIn", event.target.checked)}
                />
                Accepte les offres et nouveautés
              </label>
            </div>

            {/*
              Coordonnées de contact, tous canaux confondus.
              Le téléphone et l'e-mail ne suffisent pas : une cliente de salon
              écrit d'abord en DM Instagram. Sans son pseudo sur la fiche, on ne
              sait pas la joindre là où elle parle, et le rapprochement d'un
              futur message se refait à la main à chaque fois.
            */}
            {clientId !== null && (
              <ClientIdentities clientId={clientId} identities={detail.data?.identities ?? []} />
            )}

            {/* L'historique n'a de sens qu'une fois la fiche créée. */}
            {detail.data && detail.data.history.length > 0 && (
              <div className="mt-5">
                <h3 className="mb-2 text-sm font-medium text-encre-700">Derniers rendez-vous</h3>
                <ul className="space-y-1">
                  {detail.data.history.slice(0, 6).map((visit) => (
                    <li
                      key={visit.id}
                      className="flex justify-between gap-3 rounded-lg bg-creme-50 px-3 py-2 text-sm"
                    >
                      <span className="text-encre-700">
                        {new Intl.DateTimeFormat("fr-FR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        }).format(visit.startsAt)}{" "}
                        · {visit.lines.map((line) => line.nameSnapshot).join(" + ")}
                      </span>
                      <span className="shrink-0 tabular-nums text-encre-500">
                        {formatPrice(visit.totalPriceCents)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {save.isError && (
              <p
                role="alert"
                className="mt-4 rounded-md bg-ambre-100 px-3 py-2 text-sm font-medium text-ambre-700"
              >
                {save.error instanceof Error
                  ? save.error.message
                  : "La fiche n'a pas pu être enregistrée."}
              </p>
            )}

            <Button
              block
              size="lg"
              className="mt-5"
              loading={save.isPending}
              disabled={!draft.firstName.trim()}
              onClick={() => save.mutate()}
            >
              Enregistrer
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function emptyClientDraft() {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    birthDate: "",
    allergies: "",
    privateNote: "",
    isVip: false,
    marketingOptIn: false,
  };
}

function IdentityRow({
  identity,
  onLink,
}: {
  identity: {
    id: string;
    channel: string;
    handle: string | null;
    displayName: string | null;
    externalId: string;
    messageCount: number;
  };
  onLink: (input: { identityId: string; clientId: string }) => void;
}) {
  const [open, setOpen] = useState(false);

  const suggestions = useQuery({
    queryKey: ["admin", "suggest", identity.id],
    queryFn: () => api.admin.clients.suggestMatches.query({ identityId: identity.id }),
    enabled: open,
  });

  return (
    <li className="rounded-xl bg-white p-4 shadow-soft">
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-encre-900">
            {identity.displayName ?? identity.handle ?? identity.externalId}
          </p>
          <p className="text-sm text-encre-500">
            {identity.channel} · {identity.messageCount} message(s)
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setOpen((value) => !value)}>
          {open ? "Fermer" : "Rapprocher"}
        </Button>
      </div>

      {open && (
        <div className="mt-3 border-t border-creme-200 pt-3">
          {suggestions.isPending && <p className="text-sm text-encre-400">Recherche…</p>}

          {suggestions.data && suggestions.data.candidates.length === 0 && (
            <p className="text-sm text-encre-500">
              Aucune correspondance. Créez la fiche cliente, puis ajoutez ce contact depuis sa page.
            </p>
          )}

          <ul className="space-y-1.5">
            {suggestions.data?.candidates.map((candidate) => (
              <li key={candidate.id} className="flex items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-sm text-encre-700">
                  {shortName(candidate.firstName, candidate.lastName)}
                  <span className="text-encre-400">
                    {" "}
                    · {candidate.phone ?? candidate.email ?? "—"} · {candidate.visitCount ?? 0} visite(s)
                  </span>
                </span>
                <Badge tone={candidate.reason === "name" ? "warning" : "success"}>
                  {candidate.reason === "phone"
                    ? "même numéro"
                    : candidate.reason === "email"
                      ? "même e-mail"
                      : "nom proche"}
                </Badge>
                <Button
                  size="sm"
                  onClick={() => onLink({ identityId: identity.id, clientId: candidate.id })}
                >
                  C'est elle
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/*  Site                                                                       */
/* -------------------------------------------------------------------------- */

/** Sections que la vitrine sait afficher, avec ce qu'elles portent. */
const SECTION_KINDS = [
  { value: "hero", label: "Accroche (haut de page)" },
  { value: "highlights", label: "Points forts" },
  { value: "about", label: "À propos" },
  { value: "gallery", label: "Réalisations" },
  { value: "pricing", label: "Tarifs" },
  { value: "hours", label: "Horaires" },
  { value: "contact", label: "Contact" },
  { value: "custom", label: "Section libre" },
] as const;

function SitePanel() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState<(typeof SECTION_KINDS)[number]["value"] | null>(null);

  const sections = useQuery({
    queryKey: ["admin", "site"],
    queryFn: () => api.site.sections.query({ salonId: SALON_ID }),
  });

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.site.saveSection.mutate>[0]) =>
      api.site.saveSection.mutate(input),
    onSuccess: () => {
      setCreating(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "site"] });
    },
  });

  /*
   * La suppression existait cote API depuis toujours, sans jamais etre exposee :
   * une section ne pouvait qu'etre rendue invisible, et restait a encombrer
   * l'ecran pour l'eternite. Masquer et supprimer ne repondent pas au meme
   * besoin — l'un met de cote, l'autre corrige une erreur de saisie.
   */
  const remove = useMutation({
    mutationFn: (sectionId: string) => api.site.deleteSection.mutate({ sectionId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "site"] }),
  });

  return (
    <Panel
      title="Contenu du site"
      description="Les tarifs et les réalisations ne se saisissent pas ici : ils viennent des prestations et du portfolio, pour ne jamais être périmés."
      action={
        <select
          value=""
          onChange={(event) =>
            setCreating(event.target.value as (typeof SECTION_KINDS)[number]["value"])
          }
          className="h-9 rounded-lg border border-creme-300 bg-white px-2 text-sm"
        >
          <option value="">+ Ajouter une section…</option>
          {SECTION_KINDS.filter(
            // Une section deja presente ne se cree pas deux fois : la vitrine
            // n'en afficherait qu'une, et l'autre resterait invisible.
            (kind) => kind.value === "custom" || !sections.data?.some((s) => s.kind === kind.value),
          ).map((kind) => (
            <option key={kind.value} value={kind.value}>
              {kind.label}
            </option>
          ))}
        </select>
      }
    >
      {/* Sans ceci, une vitrine neuve restait vide pour toujours : l'ecran
          n'affichait que les sections existantes, et il n'y en avait aucune. */}
      {creating && (
        <div className="mb-2 rounded-xl bg-white p-4 shadow-soft">
          <p className="mb-2 text-sm text-encre-500">
            Nouvelle section : {SECTION_KINDS.find((k) => k.value === creating)?.label}
          </p>
          <SectionForm
            section={{ kind: creating, title: null, subtitle: null, body: null, isVisible: true }}
            pending={save.isPending}
            onSave={(values) => save.mutate({ ...values, salonId: SALON_ID, kind: creating })}
          />
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setCreating(null)}>
            Annuler
          </Button>
        </div>
      )}

      {sections.data?.length === 0 && !creating && (
        <EmptyState
          glyph="🏠"
          title="Aucune section"
          description="Le site est en ligne mais n'affiche rien. Commencez par l'accroche."
        />
      )}

      <ul className="space-y-2">
        {sections.data?.map((section) => (
          <li key={section.id} className="rounded-xl bg-white p-4 shadow-soft">
            <SectionForm
              section={section}
              sectionId={section.id}
              pending={save.isPending}
              onSave={(values) => save.mutate({ ...values, id: section.id, salonId: SALON_ID, kind: section.kind })}
            />
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 text-grenat-700"
              loading={remove.isPending}
              onClick={() => {
                // Confirmation : le texte saisi disparait sans corbeille.
                if (window.confirm(`Supprimer definitivement la section « ${section.title ?? section.kind} » ?`)) {
                  remove.mutate(section.id);
                }
              }}
            >
              Supprimer
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function SectionForm({
  section,
  sectionId,
  pending,
  onSave,
}: {
  sectionId?: string;
  section: {
    kind: string;
    navLabel?: string | null;
    title: string | null;
    subtitle: string | null;
    body: string | null;
    isVisible: boolean;
    mediaAssetId?: string | null;
    media?: { url: string } | null;
  };
  pending: boolean;
  onSave: (values: {
    navLabel: string;
    title: string;
    subtitle: string;
    body: string;
    isVisible: boolean;
    mediaAssetId: string | null;
  }) => void;
}) {
  const [draft, setDraft] = useState({
    navLabel: section.navLabel ?? "",
    title: section.title ?? "",
    subtitle: section.subtitle ?? "",
    body: section.body ?? "",
    isVisible: section.isVisible,
    mediaAssetId: section.mediaAssetId ?? null,
  });
  const [apercu, setApercu] = useState<string | null>(section.media?.url ?? null);
  const [dirty, setDirty] = useState(false);

  const set = (key: keyof typeof draft, value: string | boolean | null) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Badge tone="neutral">{sectionLabel(section.kind)}</Badge>
        {/* Le libelle du menu, court et distinct du titre : « Mes services pour
            des ongles parfaits » faisait un lien de navigation interminable. */}
        <input
          value={draft.navLabel}
          onChange={(event) => set("navLabel", event.target.value)}
          placeholder="Lien du menu (ex. Tarifs)"
          maxLength={40}
          className="h-8 w-52 rounded-lg border border-creme-300 px-2 text-sm"
        />
        <label className="ml-auto flex items-center gap-2 text-sm text-encre-600">
          <input
            type="checkbox"
            checked={draft.isVisible}
            onChange={(event) => set("isVisible", event.target.checked)}
          />
          Visible
        </label>
      </div>

      <input
        value={draft.title}
        onChange={(event) => set("title", event.target.value)}
        placeholder="Titre"
        className="h-10 w-full rounded-lg border border-creme-300 px-3 font-display text-lg"
      />
      <input
        value={draft.subtitle}
        onChange={(event) => set("subtitle", event.target.value)}
        placeholder="Sous-titre"
        className="h-10 w-full rounded-lg border border-creme-300 px-3 text-sm"
      />
      {/*
        L'image d'une section.
        `mediaAssetId` etait accepte par l'API depuis toujours, sans qu'aucun
        ecran ne permette de le renseigner : il n'existait donc AUCUN moyen de
        poser la photo de l'en-tete depuis le back-office. Faute d'image,
        l'en-tete se rabat sur les dernieres realisations du portfolio — c'est
        un repli, pas un choix.
      */}
      {section.kind === "hero" && sectionId && <HeroPhotos sectionId={sectionId} />}

      {section.kind === "about" && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-creme-200 p-3">
          {apercu ? (
            <img
              src={apercu}
              alt=""
              className="size-16 rounded-lg object-cover"
            />
          ) : (
            <span className="flex size-16 items-center justify-center rounded-lg bg-creme-100 text-xl">
              🖼️
            </span>
          )}

          <div className="min-w-0 flex-1">
            {/* « À propos » n'affiche qu'UNE image, a cote du texte : elle ne
                defile pas, et n'a donc pas besoin de la galerie de l'en-tete. */}
            <p className="text-sm text-encre-700">Photo de la section</p>
            <p className="mt-0.5 text-xs leading-relaxed text-encre-400">Facultative.</p>
          </div>

          <PhotoUpload
            label={apercu ? "Remplacer" : "Choisir une photo"}
            onUploaded={(assetId, url) => {
              set("mediaAssetId", assetId);
              setApercu(url ?? null);
            }}
          />

          {draft.mediaAssetId && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                set("mediaAssetId", null);
                setApercu(null);
              }}
            >
              Retirer
            </Button>
          )}
        </div>
      )}

      {(section.kind === "about" || section.kind === "custom") && (
        <textarea
          value={draft.body}
          onChange={(event) => set("body", event.target.value)}
          rows={5}
          placeholder="Texte (une ligne vide sépare deux paragraphes)"
          className="w-full rounded-lg border border-creme-300 p-3 text-sm leading-relaxed"
        />
      )}

      {dirty && (
        <Button
          size="sm"
          loading={pending}
          onClick={() => {
            onSave(draft);
            setDirty(false);
          }}
        >
          Enregistrer
        </Button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Éléments partagés                                                          */
/* -------------------------------------------------------------------------- */

function Panel({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl">{title}</h2>
          {description && <p className="max-w-2xl text-sm text-encre-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={className}>
      <span className="mb-1 block text-sm font-medium text-encre-700">{label}</span>
      {children}
    </label>
  );
}

const ABSENCE_LABELS: Record<string, string> = {
  break: "Pause",
  vacation: "Congé payé",
  sick_leave: "Arrêt maladie",
  training: "Formation",
  personal: "Sans solde",
  salon_closed: "Salon fermé",
  unjustified: "Injustifiée",
  family_event: "Événement familial",
  parental: "Congé parental",
  work_accident: "Accident du travail",
};
const absenceLabel = (type: string) => ABSENCE_LABELS[type] ?? type;

const SECTION_LABELS: Record<string, string> = {
  hero: "Accroche",
  about: "À propos",
  highlights: "Points forts",
  gallery: "Galerie",
  pricing: "Tarifs",
  hours: "Horaires",
  contact: "Contact",
  custom: "Section libre",
};
const sectionLabel = (kind: string) => SECTION_LABELS[kind] ?? kind;

function formatRange(from: Date, to: Date) {
  const format = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  return `${format.format(from)} → ${format.format(to)}`;
}

/* -------------------------------------------------------------------------- */
/*  Coordonnées multicanal d'une cliente                                       */
/* -------------------------------------------------------------------------- */

/** Canaux qu'on peut saisir à la main, avec ce qu'on attend dans le champ. */
const IDENTITY_CHANNELS = [
  { value: "instagram", label: "Instagram", placeholder: "@pseudo" },
  { value: "whatsapp", label: "WhatsApp", placeholder: "+33 6 12 34 56 78" },
  { value: "sms", label: "SMS", placeholder: "+33 6 12 34 56 78" },
  { value: "email", label: "E-mail", placeholder: "prenom@exemple.fr" },
  { value: "phone", label: "Téléphone", placeholder: "+33 6 12 34 56 78" },
] as const;

/**
 * Liste et saisie des coordonnées d'une cliente.
 *
 * Ces identités ne sont pas décoratives : ce sont elles qui rattachent
 * automatiquement un futur message au bon fil. Saisir le pseudo Instagram de
 * Léa aujourd'hui évite de rapprocher son DM à la main la semaine prochaine.
 */
function ClientIdentities({
  clientId,
  identities,
}: {
  clientId: string;
  identities: { id: string; channel: string; handle: string | null; externalId: string }[];
}) {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<(typeof IDENTITY_CHANNELS)[number]["value"]>("instagram");
  const [value, setValue] = useState("");

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.admin.clients.addIdentity.mutate({
        salonId: SALON_ID,
        clientId,
        channel,
        // Le « @ » est une convention d'affichage, pas une partie de
        // l'identifiant : le laisser créerait deux identités pour un compte.
        externalId: value.trim().replace(/^@/, ""),
        handle: value.trim(),
      }),
    onSuccess: () => {
      setValue("");
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (identityId: string) =>
      api.admin.clients.unlinkIdentity.mutate({ identityId }),
    onSuccess: refresh,
  });

  const current = IDENTITY_CHANNELS.find((entry) => entry.value === channel);

  return (
    <div className="mt-4 border-t border-creme-200 pt-3">
      <p className="mb-2 text-sm font-medium text-encre-700">Coordonnées et réseaux sociaux</p>

      {identities.length === 0 ? (
        <p className="mb-2 text-sm text-encre-400">
          Aucune coordonnée enregistrée pour l'instant.
        </p>
      ) : (
        <ul className="mb-3 space-y-1.5">
          {identities.map((identity) => (
            <li
              key={identity.id}
              className="flex items-center gap-2 rounded-lg bg-creme-100 px-3 py-1.5 text-sm"
            >
              <ChannelIcon channel={identity.channel as CommunicationChannel} />
              <span className="text-encre-600">
                {channelMeta[identity.channel as CommunicationChannel]?.label ?? identity.channel}
              </span>
              <span className="min-w-0 flex-1 truncate text-encre-800">
                {identity.handle ?? identity.externalId}
              </span>
              <Button
                variant="ghost"
                size="sm"
                loading={remove.isPending}
                onClick={() => remove.mutate(identity.id)}
              >
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <select
          value={channel}
          onChange={(event) => setChannel(event.target.value as typeof channel)}
          className="h-10 rounded-lg border border-creme-300 bg-white px-2 text-sm"
        >
          {IDENTITY_CHANNELS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
        <input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={current?.placeholder}
          className="h-10 min-w-0 flex-1 rounded-lg border border-creme-300 px-3 text-sm"
        />
        <Button
          variant="secondary"
          loading={add.isPending}
          disabled={!value.trim()}
          onClick={() => add.mutate()}
        >
          Ajouter
        </Button>
      </div>

      {add.error && (
        <p role="alert" className="mt-2 text-sm text-grenat-700">
          {add.error instanceof Error ? add.error.message : "Coordonnée non enregistrée."}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Journal des actions sensibles                                              */
/* -------------------------------------------------------------------------- */

const AUDIT_SCOPES = [
  { value: "tous", label: "Tout" },
  { value: "tarifs", label: "Tarifs" },
  { value: "clientes", label: "Clientes" },
  { value: "equipe", label: "Équipe" },
  { value: "horaires", label: "Horaires" },
  { value: "rendezvous", label: "Rendez-vous" },
  { value: "encaissement", label: "Encaissement" },
] as const;

/** Ton du badge selon la portée du geste. */
const ACTION_TONE: Record<string, "danger" | "warning" | "success" | "neutral"> = {
  "client.merged": "danger",
  "service.deleted": "danger",
  "staff.deactivated": "danger",
  "staff.password_reset": "danger",
  "appointment.cancelled": "danger",
  "client.identity_removed": "warning",
  "hours.replaced": "warning",
  "service.updated": "warning",
  "client.updated": "warning",
  "staff.updated": "warning",
  "payment.collected": "success",
  "service.created": "neutral",
  "client.created": "neutral",
  "staff.created": "neutral",
  "staff.reactivated": "neutral",
  "client.identity_added": "neutral",
};

function AuditPanel() {
  const [scope, setScope] = useState<(typeof AUDIT_SCOPES)[number]["value"]>("tous");
  const [actorId, setActorId] = useState<string>("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const journal = useQuery({
    queryKey: ["admin", "audit", scope, actorId],
    queryFn: () =>
      api.admin.audit.list.query({
        salonId: SALON_ID,
        scope,
        actorId: actorId || undefined,
      }),
  });

  const actors = useQuery({
    queryKey: ["admin", "audit", "actors"],
    queryFn: () => api.admin.audit.actors.query({ salonId: SALON_ID }),
  });

  return (
    <Panel
      title="Journal"
      description="Qui a fait quoi, sur les gestes qui engagent : tarifs, fiches clientes, équipe, horaires, annulations, encaissement. En ajout seul — rien ne s'y modifie ni ne s'y efface."
      action={
        <select
          value={actorId}
          onChange={(event) => setActorId(event.target.value)}
          className="h-9 rounded-lg border border-creme-300 bg-white px-2 text-sm"
        >
          <option value="">Toute l'équipe</option>
          {(actors.data ?? [])
            .filter((entry) => entry.id)
            .map((entry) => (
              <option key={entry.id} value={entry.id!}>
                {entry.name}
              </option>
            ))}
        </select>
      }
    >
      <div className="mb-3 flex flex-wrap gap-1">
        {AUDIT_SCOPES.map((entry) => (
          <button
            key={entry.value}
            type="button"
            onClick={() => setScope(entry.value)}
            aria-pressed={scope === entry.value}
            className={[
              "rounded-full px-3 py-1.5 text-sm transition-colors",
              scope === entry.value
                ? "bg-blush-500 font-medium text-white"
                : "text-encre-600 hover:bg-creme-200",
            ].join(" ")}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {journal.isPending && <p className="text-encre-400">Chargement…</p>}

      {journal.data && journal.data.items.length === 0 && (
        <EmptyState
          glyph="📋"
          title="Rien à signaler"
          description="Aucune action sensible enregistrée sur cette période."
        />
      )}

      <ul className="space-y-1.5">
        {journal.data?.items.map((entry) => {
          const open = expanded === entry.id;
          // Le détail n'est pas toujours utile : on ne l'ouvre que si l'avant
          // ou l'après existe, sinon le chevron ment sur ce qu'il cache.
          const hasDetail = entry.before != null || entry.after != null;

          return (
            <li key={entry.id} className="rounded-xl bg-white p-3 shadow-soft">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Badge tone={ACTION_TONE[entry.action] ?? "neutral"}>{entry.action}</Badge>
                <span className="min-w-0 flex-1 text-encre-800">{entry.summary}</span>
                <span className="text-sm text-encre-500">{entry.actorName}</span>
                <time
                  dateTime={new Date(entry.createdAt).toISOString()}
                  className="text-xs text-encre-400"
                >
                  {new Intl.DateTimeFormat("fr-FR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }).format(new Date(entry.createdAt))}
                </time>
                {hasDetail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded(open ? null : entry.id)}
                  >
                    {open ? "Masquer" : "Détail"}
                  </Button>
                )}
              </div>

              {open && (
                <div className="mt-2 grid gap-2 border-t border-creme-200 pt-2 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-medium text-encre-500">Avant</p>
                    <pre className="overflow-x-auto rounded-lg bg-creme-100 p-2 text-xs text-encre-700">
                      {entry.before ? JSON.stringify(entry.before, null, 2) : "—"}
                    </pre>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-encre-500">Après</p>
                    <pre className="overflow-x-auto rounded-lg bg-creme-100 p-2 text-xs text-encre-700">
                      {entry.after ? JSON.stringify(entry.after, null, 2) : "—"}
                    </pre>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/*  Galerie de réalisations                                                    */
/* -------------------------------------------------------------------------- */

/**
 * La galerie n'existait qu'en lecture : le site savait l'afficher, personne ne
 * pouvait la remplir. Elle porte pourtant le tunnel de réservation — « je veux
 * CE nail art » est la porte d'entrée la plus fréquente.
 */
function PortfolioPanel() {
  const queryClient = useQueryClient();

  const photos = useQuery({
    queryKey: ["admin", "portfolio"],
    queryFn: () => api.admin.portfolio.list.query({ salonId: SALON_ID }),
  });

  const services = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api.admin.catalog.services.query({ salonId: SALON_ID }),
  });

  const categories = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => api.admin.catalog.categories.query({ salonId: SALON_ID }),
  });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ["admin", "portfolio"] });

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.admin.portfolio.save.mutate>[0]) =>
      api.admin.portfolio.save.mutate(input),
    onSuccess: rafraichir,
  });

  const remove = useMutation({
    mutationFn: (itemId: string) => api.admin.portfolio.remove.mutate({ itemId }),
    onSuccess: rafraichir,
  });

  return (
    <Panel
      title="Réalisations"
      description="Ces photos alimentent la galerie du site. Une cliente qui en touche une arrive sur la réservation avec la prestation déjà choisie."
      action={
        <PhotoUpload
          onUploaded={(assetId) => save.mutate({ salonId: SALON_ID, assetId })}
        />
      }
    >
      {photos.data?.length === 0 && (
        <EmptyState
          glyph="📷"
          title="Aucune réalisation"
          description="C'est la galerie qui fait cliquer : une photo vaut mieux qu'une description."
        />
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {photos.data?.map((photo) => (
          <figure key={photo.id} className="overflow-hidden rounded-xl bg-white shadow-soft">
            <img
              src={photo.asset.thumbnailUrl ?? photo.asset.url}
              alt={photo.asset.altText ?? ""}
              loading="lazy"
              // La couleur dominante evite que la case reste blanche puis
              // saute quand la photo arrive.
              style={{ backgroundColor: photo.asset.dominantColor ?? undefined }}
              className="aspect-square w-full object-cover"
            />
            <figcaption className="space-y-2 p-2.5">
              {/* La prestation representee : c'est elle qui pre-remplit la
                  reservation quand la cliente touche la photo. */}
              <select
                value={photo.serviceId ?? ""}
                onChange={(event) =>
                  save.mutate({
                    id: photo.id,
                    salonId: SALON_ID,
                    assetId: photo.asset.id,
                    serviceId: event.target.value || null,
                  })
                }
                className="h-9 w-full rounded-lg border border-creme-300 bg-white px-2 text-sm"
              >
                <option value="">Prestation…</option>
                {services.data?.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 text-xs text-encre-600">
                  <input
                    type="checkbox"
                    checked={photo.isPublic}
                    onChange={(event) =>
                      save.mutate({
                        id: photo.id,
                        salonId: SALON_ID,
                        assetId: photo.asset.id,
                        isPublic: event.target.checked,
                      })
                    }
                  />
                  Visible
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-grenat-700"
                  onClick={() => {
                    if (window.confirm("Retirer cette photo de la galerie ?")) {
                      remove.mutate(photo.id);
                    }
                  }}
                >
                  Retirer
                </Button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/*  Rappels                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Reglage des messages envoyes aux clientes.
 *
 * Un interrupteur par SMS, confirmation et rappel : chacun se facture a
 * l'envoi et s'impose au telephone de la cliente. Ce choix appartient au
 * salon, il ne doit pas se decouvrir sur une facture — ni demander un
 * redeploiement pour etre annule.
 */
function RemindersPanel() {
  const queryClient = useQueryClient();

  const reglages = useQuery({
    queryKey: ["salon-settings"],
    queryFn: () => api.site.salonSettings.query({ salonId: SALON_ID }),
  });

  const enregistrer = useMutation({
    mutationFn: (champs: {
      smsReminder?: boolean;
      smsConfirmation?: boolean;
      hideStaffNames?: boolean;
    }) =>
      api.site.saveSalon.mutate({
        salonId: SALON_ID,
        /*
         * On renvoie la politique ENTIERE.
         *
         * `saveSalon` remplace `booking_policy` d'un bloc : n'envoyer que la
         * cle modifiee effacerait le delai minimum, la fenetre d'annulation et
         * la confirmation automatique.
         */
        bookingPolicy: { ...reglages.data!.bookingPolicy, ...champs },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["salon-settings"] }),
  });

  if (reglages.isPending) {
    return <div className="h-28 animate-pulse rounded-xl bg-creme-200" />;
  }

  const actif = Boolean(reglages.data?.bookingPolicy?.smsReminder);
  const confirmationActive = Boolean(reglages.data?.bookingPolicy?.smsConfirmation);
  const nomsMasques = Boolean(reglages.data?.bookingPolicy?.hideStaffNames);

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-creme-200 bg-creme-50 p-5">
        <h2 className="text-lg">Prise de rendez-vous</h2>

        <label className="mt-4 flex items-start gap-3">
          <input
            type="checkbox"
            checked={nomsMasques}
            disabled={enregistrer.isPending}
            onChange={(event) => enregistrer.mutate({ hideStaffNames: event.target.checked })}
            className="mt-1 size-4"
          />
          <span>
            <span className="block text-encre-900">Masquer les noms des employées</span>
            <span className="mt-1 block text-sm leading-relaxed text-encre-500">
              La cliente choisit une heure, pas une personne : c&apos;est vous qui répartissez.
              Un horaire n&apos;apparaît alors qu&apos;une fois, même si deux employées sont
              libres — et il reste proposé tant qu&apos;au moins l&apos;une d&apos;elles peut le
              prendre.
            </span>
          </span>
        </label>
      </div>

      <div className="rounded-xl border border-creme-200 bg-creme-50 p-5">
        <h2 className="text-lg">Confirmation de réservation</h2>
        <p className="mt-2 text-sm leading-relaxed text-encre-600">
          Chaque cliente qui a laissé son adresse reçoit un e-mail dès que le rendez-vous est
          confirmé, avec les liens pour modifier ou annuler.
        </p>

        <label className="mt-5 flex items-start gap-3">
          <input
            type="checkbox"
            checked={confirmationActive}
            disabled={enregistrer.isPending}
            onChange={(event) => enregistrer.mutate({ smsConfirmation: event.target.checked })}
            className="mt-1 size-4"
          />
          <span>
            <span className="block text-encre-900">Doubler d&apos;un SMS</span>
            <span className="mt-1 block text-sm leading-relaxed text-encre-500">
              Un SMS court part en même temps que l&apos;e-mail, aux clientes dont on a le
              numéro : le jour, l&apos;heure, l&apos;employée et le numéro de réservation. Une
              demande en attente n&apos;en reçoit qu&apos;une fois acceptée.
            </span>
          </span>
        </label>
      </div>

      <div className="rounded-xl border border-creme-200 bg-creme-50 p-5">
        <h2 className="text-lg">Rappel de la veille</h2>
        <p className="mt-2 text-sm leading-relaxed text-encre-600">
          Chaque cliente qui a laissé son adresse reçoit un e-mail la veille à 18 h, avec les
          liens pour modifier ou annuler.
        </p>

        <label className="mt-5 flex items-start gap-3">
          <input
            type="checkbox"
            checked={actif}
            disabled={enregistrer.isPending}
            onChange={(event) => enregistrer.mutate({ smsReminder: event.target.checked })}
            className="mt-1 size-4"
          />
          <span>
            <span className="block text-encre-900">Doubler d&apos;un SMS</span>
            <span className="mt-1 block text-sm leading-relaxed text-encre-500">
              Un SMS court part en même temps que l&apos;e-mail, aux clientes dont on a le
              numéro. Il rappelle l&apos;heure, l&apos;employée et le numéro de réservation, sans lien : un lien
              de gestion ferait passer le message à deux SMS, donc au double du prix.
            </span>
          </span>
        </label>

        {enregistrer.isError && (
          <p role="alert" className="mt-3 text-sm text-grenat-700">
            {enregistrer.error instanceof Error
              ? enregistrer.error.message
              : "Enregistrement impossible."}
          </p>
        )}
      </div>

      {(actif || confirmationActive) && (
        <p className="text-sm leading-relaxed text-encre-500">
          Les SMS ne partiront que si Twilio est configuré sur l&apos;API <em>et</em> sur le worker.
          Pour le vérifier, lancez <code className="text-encre-700">verify-sms</code> sur le
          serveur.
        </p>
      )}
    </div>
  );
}

/**
 * Les photos de l'en-tete : jusqu'a cinq, qui defilent en fondu sur le site.
 *
 * Separe du formulaire de la section, et volontairement : ces images
 * s'enregistrent CHACUNE a l'ajout, sans passer par le bouton « Enregistrer »
 * du texte. Melanger les deux obligerait a valider un formulaire pour qu'une
 * photo deja televersee soit prise en compte — et on oublierait de le faire.
 */
function HeroPhotos({ sectionId }: { sectionId: string }) {
  const queryClient = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);
  /*
   * La vignette saisie vit dans une REFERENCE, pas dans un etat.
   *
   * `onDrop` lit la valeur posee par `onDragStart`. Un etat n'est visible du
   * gestionnaire qu'apres un rendu ; en usage reel il y en a un — l'utilisateur
   * deplace la souris entre les deux — mais faire dependre la justesse d'un
   * depot du fait qu'un rendu s'est intercale, c'est le defaut qui a coute la
   * selection en vue jour du planning. La reference est lue a l'instant ou on
   * en a besoin.
   *
   * L'etat, lui, ne sert qu'a l'apparence : vignette estompee, cible soulignee.
   */
  const saisieRef = useRef<number | null>(null);
  const [saisie, setSaisie] = useState<number | null>(null);
  const [survol, setSurvol] = useState<number | null>(null);

  const photos = useQuery({
    queryKey: ["section-media", sectionId],
    queryFn: () => api.site.sectionMedia.query({ sectionId }),
  });

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ["section-media", sectionId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "site"] });
  };

  const ajouter = useMutation({
    mutationFn: (assetId: string) => api.site.addSectionMedia.mutate({ sectionId, assetId }),
    onSuccess: () => {
      setErreur(null);
      rafraichir();
    },
    onError: (error) => setErreur(error instanceof Error ? error.message : "Ajout impossible."),
  });

  const retirer = useMutation({
    mutationFn: (assetId: string) => api.site.removeSectionMedia.mutate({ sectionId, assetId }),
    onSuccess: rafraichir,
  });

  const reordonner = useMutation({
    mutationFn: (order: string[]) => api.site.reorderSectionMedia.mutate({ sectionId, order }),
    /*
     * On reecrit le cache TOUT DE SUITE, sans attendre le serveur.
     *
     * Un glisser dont la vignette revient a sa place le temps d'un
     * aller-retour reseau donne l'impression que le geste a rate, et on
     * recommence. Le serveur ne fait ensuite que confirmer.
     */
    onMutate: async (order) => {
      await queryClient.cancelQueries({ queryKey: ["section-media", sectionId] });
      const precedent = queryClient.getQueryData<Photo[]>(["section-media", sectionId]);
      if (precedent) {
        queryClient.setQueryData<Photo[]>(
          ["section-media", sectionId],
          order
            .map((assetId) => precedent.find((photo) => photo.assetId === assetId))
            .filter((photo): photo is Photo => Boolean(photo)),
        );
      }
      return { precedent };
    },
    onError: (_error, _order, contexte) => {
      // Retour a l'ordre d'avant : mieux vaut revenir en arriere que laisser
      // l'ecran affirmer un ordre que la base ignore.
      if (contexte?.precedent) {
        queryClient.setQueryData(["section-media", sectionId], contexte.precedent);
      }
      setErreur("Impossible d'enregistrer l'ordre.");
    },
    onSuccess: rafraichir,
  });

  const liste = photos.data ?? [];
  const complet = liste.length >= 5;

  /** Deplace la vignette `de` vers la position `vers`, et enregistre. */
  const deplacer = (de: number, vers: number) => {
    if (de === vers || vers < 0 || vers >= liste.length) return;
    const ordre = liste.map((photo) => photo.assetId);
    const [deplacee] = ordre.splice(de, 1);
    ordre.splice(vers, 0, deplacee!);
    reordonner.mutate(ordre);
  };

  return (
    <div className="rounded-lg border border-creme-200 p-3">
      <p className="text-sm text-encre-700">Photos de l&apos;en-tête</p>
      <p className="mt-0.5 text-xs leading-relaxed text-encre-400">
        Jusqu&apos;à cinq, elles se succèdent en fondu. Faites-les glisser pour changer
        l&apos;ordre — ou sélectionnez-en une et utilisez les flèches. Sans photo,
        l&apos;en-tête affiche les quatre dernières réalisations du portfolio.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {liste.map((photo, index) => (
          <div
            key={photo.assetId}
            /*
             * Glisser-deposer natif du navigateur.
             *
             * Suffisant pour cinq vignettes, et sans le piege du planning :
             * aucun ecouteur pose sur `window`, donc aucune identite de
             * fonction a resynchroniser entre deux rendus.
             */
            draggable
            onDragStart={(event) => {
              saisieRef.current = index;
              setSaisie(index);
              event.dataTransfer.effectAllowed = "move";
              // Firefox n'amorce pas un glisser sans donnee transportee.
              event.dataTransfer.setData("text/plain", photo.assetId);
            }}
            onDragOver={(event) => {
              // Sans `preventDefault`, la zone refuse le depot.
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setSurvol(index);
            }}
            onDragLeave={() => setSurvol((actuel) => (actuel === index ? null : actuel))}
            onDrop={(event) => {
              event.preventDefault();
              const depuis = saisieRef.current;
              if (depuis !== null) deplacer(depuis, index);
              saisieRef.current = null;
              setSaisie(null);
              setSurvol(null);
            }}
            onDragEnd={() => {
              saisieRef.current = null;
              setSaisie(null);
              setSurvol(null);
            }}
            className={[
              "relative rounded-lg transition-[transform,opacity] duration-[160ms]",
              saisie === index ? "opacity-40" : "",
              survol === index && saisie !== null && saisie !== index
                ? "ring-2 ring-blush-400 ring-offset-2"
                : "",
            ].join(" ")}
          >
            {/*
              La vignette est un bouton, et pas seulement pour le style : sans
              elle, l'ordre ne se modifierait qu'a la souris. Les fleches
              gauche et droite font le meme travail au clavier.
            */}
            <button
              type="button"
              aria-label={`Photo ${index + 1} sur ${liste.length} — flèches gauche et droite pour déplacer`}
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                deplacer(index, index + (event.key === "ArrowLeft" ? -1 : 1));
              }}
              className="block cursor-grab active:cursor-grabbing"
            >
              <img
                src={photo.url}
                alt=""
                draggable={false}
                className="size-16 rounded-lg object-cover"
              />
            </button>

            <span className="pointer-events-none absolute left-1 top-1 rounded bg-encre-900/70 px-1 text-[10px] font-medium text-white">
              {index + 1}
            </span>

            <button
              type="button"
              aria-label="Retirer cette photo"
              onClick={() => retirer.mutate(photo.assetId)}
              className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-encre-900 text-xs text-white"
            >
              ×
            </button>
          </div>
        ))}

        {!complet && <PhotoUpload label="+ Photo" onUploaded={(assetId) => ajouter.mutate(assetId)} />}
      </div>

      {complet && (
        <p className="mt-2 text-xs text-encre-400">
          Cinq photos, c&apos;est le maximum. Retirez-en une pour en ajouter une autre.
        </p>
      )}

      {erreur && (
        <p role="alert" className="mt-2 text-sm text-grenat-700">
          {erreur}
        </p>
      )}
    </div>
  );
}

type Photo = { assetId: string; sortOrder: number; url: string };
